import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";

import { AuthService } from "./../auth.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { RecaptchaComponent } from "ng-recaptcha";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-create-account",
  templateUrl: "./create-account.component.html",
})
export class CreateAccountComponent implements OnInit, OnDestroy {
  createAccountForm: UntypedFormGroup;
  createAccountError: string;

  submitted = false;

  public createAccountSuccess = false;
  public myMessage = "";

  public readonly siteKey = "6LfyV20aAAAAADgafr-4CKTNvUpqd3NEsZ4nwmfB";

  public captchaIsLoaded = false;
  public captchaSuccess = false;
  public captchaIsExpired = false;
  public captchaResponse: string | null = null;
  @ViewChild('captchaElem') captchaElem?: RecaptchaComponent;

  handleSuccess(token: string | null): void {
    this.captchaSuccess = !!token;
    this.captchaResponse = token;
    this.captchaIsExpired = !token;
    this.cdr.detectChanges();
  }
  private destroy$ = new Subject<void>();

  formErrors = {
    email: "",
    ssn: "",
    confirmSsn: "",
    zipCode: "",
    password: "",
    confirmPassword: "",
  };

  private formValues = {
    email: "",
    ssn: "",
    confirmSsn: "",
    zipCode: "",
    password: "",
    confirmPassword: "",
    recaptcha: "",
  };

  private validationMessages = {
    email: {
      required: "Email is required.",
      email: "Email must be in the correct format.",
      maxlength: "Email must be max 100 characters.",
    },
    ssn: {
      required: "SSN is required.",
      ssnComplexity: "SSN must be in the correct format",
      maxlength: "SSN must be 9 characters.",
      minlength: "SSN must be 9 characters.",
    },
    confirmSsn: {
      required: "SSN Confirmation is required.",
      appValidateEqual: "SSN and Confirm SSN must match.",
    },
    zipCode: {
      required: "ZIP Code is required.",
      minlength: "Zip / Postal Code must be min 5 characters.",
      maxlength: "Zip / Postal Code must be max 10 characters.",
      zipCodeComplexity: "Zip / Postal Code must be in the correct format",
    },
    password: {
      required: "Password is required.",
      passwordComplexity:
        "Password must meet complexity requirements (one number, one capital letter, one symbol, 8 characters).",
    },
    confirmPassword: {
      required: "Password Confirmation is required.",
      appValidateEqual: "Password and Confirm Password must match.",
    },
    form: "The information you submitted is not in our records. Please verify all information is entered correctly. If you continue to have issues, please contact our office at info@nbrc.org or 913.895.4900 for assistance.",
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private viewState: ViewStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.formValues = updatedFormValues;
    this.validateForm();
    this.handleFormResult();
  }

  private buildForm(): void {
    this.createAccountForm = this.formBuilder.group({
      recaptcha: ["", Validators.required],
      email: [
        this.formValues.email,
        [Validators.required, Validators.email, Validators.maxLength(100)],
      ],
      ssn: [
        this.formValues.ssn,
        [
          Validators.required,
          Validators.maxLength(9),
          Validators.minLength(9),
          this.ssnComplexity,
        ],
      ],
      zipCode: [
        this.formValues.zipCode,
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(10),
          //this.zipCodeComplexity
        ],
      ]
    });
  }

  private validateForm() {
    if (!this.createAccountForm) {
      return;
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = "";
      const control = this.createAccountForm.get(field);
      if (control && !control.valid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] = messages[key];
          break;
        }
      }
    }

    if (hasErrors) {this.viewState.stopLoading();}
  }

  private handleFormResult() {
    if (!this.captchaSuccess) {
      alert("Are you not a robot?");
      this.viewState.stopLoading();
      return;
    }
    this.createAccountError = "";
    if (this.createAccountForm.valid) {
      this.authService
        .register(
          this.formValues.email,
          this.formValues.zipCode,
          this.formValues.ssn,
          this.formValues.recaptcha
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (ret) => {
            if (ret.message === "New") {
              this.myMessage = "If you are a recent or soon to be graduate you must wait to create an account until you have graduated and your program has sent your record to NBRC. You will receive an email notification with a temporary password when we receive your graduation information. If you believe your record has already been sent, please contact your Program Director to confirm the email address and social security number (or assigned identification number) for your record.";
            } else if (ret.message === "AlreadyExists") {
              this.myMessage =
                "An account with that email address already exists. Please check what you've entered. You can use forgot password if you have forgotten how to log into your account.";
            } else if (ret.message === "CallIn") {
              this.myMessage =
                "Information located but unable to access online.  Please call into 913.895.4900 to enable online access to your account.";
            } else {
              this.myMessage =
                "Thank you for creating your Practitioner Portal account. An email was sent to you to activate your account. Once your confirm your email address, you can login using the email and password you selected during the registration process.";
              this.createAccountSuccess = true;
            }
            this.handleReset();
            this.viewState.stopLoading();
          },
          (_e) => {
            this.createAccountError = this.validationMessages.form;
            this.viewState.stopLoading();
          }
        );
    }
  }

  private ssnComplexity(c: UntypedFormControl) {
    if (!c.value) {return null;}

    let valid = true;
    if (!c.value.match(/^(F|f|NB|nb|T|t|RC|rc)?[0-9\-]+$/)) {valid = false;}

    if (valid) {return null;}

    return { ssnComplexity: { valid: "invalid" } };
  }

  handleReset(): void {
    this.captchaSuccess = false;
    this.captchaResponse = null;
    this.captchaIsExpired = false;
    this.cdr.detectChanges();
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
